import { create } from 'zustand'
import type { AdminUser, MenuItem } from '@/types'
import { authApi } from '@/api'

interface AuthState {
  token: string
  user: AdminUser | null
  roles: string[]
  permissions: string[]
  menus: MenuItem[]
  loaded: boolean

  setToken: (token: string) => void
  fetchUserInfo: () => Promise<void>
  logout: () => void
  hasPermission: (perm: string) => boolean
  isSuperAdmin: () => boolean
  isPathAllowed: (pathname: string) => boolean
}

const ALWAYS_ALLOWED_PATHS = ['/dashboard', '/merchant-dashboard', '/login']

function collectMenuPaths(menus: MenuItem[], acc: Set<string>) {
  menus.forEach(item => {
    if (item.type !== 3 && item.path) acc.add(item.path)
    if (item.children?.length) collectMenuPaths(item.children, acc)
  })
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: localStorage.getItem('token') || '',
  user: null,
  roles: [],
  permissions: [],
  menus: [],
  loaded: false,

  setToken: (token: string) => {
    localStorage.setItem('token', token)
    set({ token })
  },

  fetchUserInfo: async () => {
    try {
      const info = await authApi.getInfo()
      set({
        user: info.user,
        roles: info.roles ?? [],
        permissions: info.permissions ?? [],
        menus: info.menus ?? [],
        loaded: true,
      })
    } catch {
      get().logout()
    }
  },

  logout: () => {
    localStorage.removeItem('token')
    set({
      token: '',
      user: null,
      roles: [],
      permissions: [],
      menus: [],
      loaded: false,
    })
    window.location.href = '/login'
  },

  hasPermission: (perm: string) => {
    const { permissions, roles } = get()
    if (roles.includes('super_admin')) return true
    return permissions.includes(perm) || permissions.includes('*')
  },

  isSuperAdmin: () => get().roles.includes('super_admin'),

  isPathAllowed: (pathname: string) => {
    if (ALWAYS_ALLOWED_PATHS.includes(pathname)) return true
    const { roles, menus } = get()
    if (roles.includes('super_admin')) return true
    const allowed = new Set<string>()
    collectMenuPaths(menus, allowed)
    if (allowed.has(pathname)) return true
    for (const path of allowed) {
      if (pathname.startsWith(`${path}/`)) return true
    }
    return false
  },
}))
